import HelpFlyout from "./HelpFlyout.jsx";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, getAccount, config as configApi } from "../lib/supabase.js";
import { useUserRole, hasPermission } from "../App.jsx";

// ─── Navigation ──────────────────────────────────────────────────────────────
// Two sections separated by a boxed divider, Walker-style.
// Top: primary pages. Bottom (after divider): admin/config pages.

const primaryNav = [
  { path: "/", label: "Overview", perm: "viewContent" },
  { path: "/conversations", label: "Conversations", perm: "viewConversations" },
  { path: "/leads", label: "Leads", perm: "viewConversations" },
  { path: "/documents", label: "Documents", perm: "viewContent" },
  { path: "/products", label: "Products", perm: "viewContent" },
  { path: "/locations", label: "Locations", perm: "viewContent" },
  { path: "/analytics", label: "Analytics", perm: "viewAnalytics" },
  { path: "/reports", label: "Reports", perm: "viewAnalytics" },
  { path: "/agents", label: "Agents", perm: "viewAnalytics" },
];

const secondaryNav = [
  { path: "/widget", label: "Chat Widget", perm: "editContent" },
  { path: "/training", label: "Training", perm: "trainAssistant" },
  { path: "/document-widget", label: "Doc Widget", perm: "editContent" },
  { path: "/floor", label: "Floor Access", perm: "viewContent" },
  { path: "/api-docs", label: "API & MCP", perm: "viewContent" },
  { path: "/team", label: "Team", perm: "viewContent" },
  { path: "/billing", label: "Billing", perm: "manageBilling" },
  { path: "/settings", label: "Settings", perm: "viewContent" },
];

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

// ─── Component ───────────────────────────────────────────────────────────────

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const account = getAccount();
  const { role } = useUserRole();

  const logoUrl = account?.theme_logo_url || account?.widget_logo_url || null;
  const logoWidth = account?.theme_logo_width || 140;

  const [aiProvider, setAiProvider] = useState(null);
  const [aiModel, setAiModel] = useState(null);

  useEffect(() => {
    configApi.get().then(({ data }) => {
      if (data) { setAiProvider(data.ai_provider || null); setAiModel(data.ai_model || null); }
    });
  }, []);

  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);

  useEffect(() => {
    if (account?.theme_favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = account.theme_favicon_url;
    }
  }, [account?.theme_favicon_url]);

  const visiblePrimary = primaryNav.filter(item => hasPermission(role, item.perm));
  const visibleSecondary = secondaryNav.filter(item => hasPermission(role, item.perm));

  const NavButton = ({ item }) => {
    const isActive = location.pathname === item.path;
    return (
      <button
        onClick={() => navigate(item.path)}
        style={{
          display: "block", width: "100%", padding: "6px 0",
          background: "none", border: "none",
          color: isActive ? "#1a1a1a" : "#8a8a8a",
          fontSize: 13, fontWeight: isActive ? 700 : 500,
          letterSpacing: "0.04em", textTransform: "uppercase",
          cursor: "pointer", textAlign: "left", fontFamily: sans,
          transition: "color 0.12s",
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "#444444"; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "#8a8a8a"; }}
      >
        {item.label}
      </button>
    );
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: sans, background: "#fafaf8", color: "#1a1a1a" }}>
      {/* ─── Sidebar ─── */}
      <nav style={{
        width: 200, minHeight: "100vh", background: "#ffffff",
        borderRight: "1px solid #e5e3de", padding: "28px 0",
        display: "flex", flexDirection: "column", flexShrink: 0,
      }}>
        {/* Identity */}
        <div style={{ padding: "0 24px", marginBottom: 36 }}>
          {logoUrl ? (
            <div>
              <img
                src={logoUrl} alt={account?.name || ""}
                style={{ maxWidth: Math.min(logoWidth, 150), maxHeight: 34, objectFit: "contain", display: "block" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          ) : (
            <div style={{
              fontSize: 18, fontWeight: 400, fontFamily: serif, fontStyle: "italic",
              lineHeight: 1.2,
            }}>
              {account?.name || "Clarifico"}
            </div>
          )}
        </div>

        {/* Utility row — plan + account type */}
        <div style={{
          padding: "0 24px", marginBottom: 24, display: "flex", flexDirection: "column",
          alignItems: "flex-start", gap: 4,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 600, textTransform: "uppercase",
            letterSpacing: "0.1em", color: "#8a8a8a",
          }}>
            {account?.plan} plan
          </span>
        </div>

        {/* Primary nav */}
        <div style={{ padding: "0 24px", marginBottom: 8 }}>
          {visiblePrimary.map((item) => <NavButton key={item.path} item={item} />)}
        </div>

        {/* Divider — Walker "MORE THAN A MUSEUM" style */}
        {visibleSecondary.length > 0 && (
          <div style={{ padding: "16px 24px 12px" }}>
            <div style={{
              display: "inline-block", padding: "4px 10px",
              border: "1px solid #e5e3de", fontSize: 9, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: "0.12em", color: "#8a8a8a",
            }}>
              Configure
            </div>
          </div>
        )}

        {/* Secondary nav */}
        <div style={{ padding: "0 24px" }}>
          {visibleSecondary.map((item) => <NavButton key={item.path} item={item} />)}
        </div>

        <div style={{ flex: 1 }} />

        {/* AI Engine — minimal */}
        <div style={{ padding: "0 24px", marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: "#c0bdb8", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 3 }}>
            AI engine
          </div>
          <div style={{ fontSize: 11, color: "#8a8a8a", fontWeight: 500 }}>
            {aiProvider || account?.ai_provider || "openai"} / {aiModel || account?.ai_model || "gpt-4o-mini"}
          </div>
        </div>

        {/* Sign out — just text, Walker-style */}
        <div style={{ padding: "0 24px" }}>
          <button
            onClick={() => auth.signOut()}
            style={{
              background: "none", border: "none", padding: 0,
              color: "#c0bdb8", fontSize: 11, fontWeight: 500,
              cursor: "pointer", fontFamily: sans,
              textTransform: "uppercase", letterSpacing: "0.06em",
              transition: "color 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#1a1a1a"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#c0bdb8"; }}
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* ─── Main ─── */}
      <main style={{ flex: 1, padding: 28, overflowY: "auto", maxHeight: "100vh" }}>
        {children}
      </main>
      <HelpFlyout />
    </div>
  );
}
